import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield, BookOpen, ArrowRight, Clock, ChevronRight } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import SEO, { organizationSchema, createBreadcrumbSchema } from "../components/SEO";

const guidesData = {
  pt: [
    {
      id: 1,
      slug: "canal-denuncias-ouvidoria-obrigatorio-smb",
      title: "Canal de Denúncias: É Obrigatório para PMEs?",
      description: "Descubra quando sua PME precisa de canal de denúncias por lei. Evite multas e riscos trabalhistas. Implemente em minutos. Saiba mais agora!",
      readTime: "5 min"
    },
    {
      id: 2,
      slug: "diferenca-canal-denuncias-ouvidoria",
      title: "Denúncia vs Ouvidoria: Qual a Diferença?",
      description: "Aprenda as diferenças entre canal de denúncias e ouvidoria corporativa. Saiba quando usar cada um e fortaleça sua governança. Descubra agora!",
      readTime: "4 min"
    },
    {
      id: 3,
      slug: "como-implementar-canal-denuncias-ouvidoria-smb",
      title: "Como Implementar Canal de Denúncias em PMEs",
      description: "Passo a passo para criar canal de denúncias e ouvidoria na sua PME. Sem burocracia, pronto em minutos. Comece gratuitamente agora!",
      readTime: "6 min"
    },
    {
      id: 4,
      slug: "como-ombudnow-fortalece-compliance-empresarial",
      title: "OmbudNow: Compliance com Denúncia e Ouvidoria",
      description: "Veja como o OmbudNow integra ouvidoria e canal de denúncias para fortalecer seu compliance. Reduza riscos e proteja sua empresa. Teste grátis!",
      readTime: "8 min"
    }
  ],
  en: [
    {
      id: 1,
      slug: "are-whistleblowing-and-ombudsman-channels-required-for-smbs",
      title: "Whistleblowing Channels: Required for SMBs?",
      description: "Find out when your SMB needs a whistleblowing channel by law. Avoid fines and legal risks. Set up in minutes. Learn more now!",
      readTime: "5 min"
    },
    {
      id: 2,
      slug: "whats-the-difference-between-a-whistleblowing-channel-and-an-ombudsman-channel",
      title: "Whistleblowing vs Ombudsman: Key Differences",
      description: "Learn the key differences between whistleblowing and ombudsman channels. Know when to use each and strengthen your governance. Find out now!",
      readTime: "4 min"
    },
    {
      id: 3,
      slug: "how-to-implement-a-whistleblowing-and-ombudsman-channel-in-an-smb",
      title: "How to Implement Whistleblowing in SMBs",
      description: "Step-by-step guide to create whistleblowing and ombudsman channels in your SMB. No bureaucracy, ready in minutes. Start free now!",
      readTime: "6 min"
    },
    {
      id: 4,
      slug: "how-ombudnow-strengthens-business-compliance",
      title: "OmbudNow: Strengthen Your Business Compliance",
      description: "See how OmbudNow integrates ombudsman and whistleblowing to strengthen your compliance. Reduce risks and protect your business. Try free!",
      readTime: "8 min"
    }
  ]
};

const content = {
  pt: {
    title: "Guias e Artigos",
    subtitle: "Conteúdo especializado sobre compliance, canal de denúncias e ouvidoria para pequenas e médias empresas.",
    readMore: "Ler artigo",
    readTime: "leitura",
    metaTitle: "Guias sobre Canal de Denúncias e Ouvidoria | OmbudNow",
    metaDescription: "Guias completos sobre canal de denúncias e ouvidoria para pequenas e médias empresas. Aprenda sobre obrigatoriedade, diferenças e implementação."
  },
  en: {
    title: "Guides & Articles",
    subtitle: "Specialized content about compliance, whistleblowing channels, and ombudsman for small and medium businesses.",
    readMore: "Read article",
    readTime: "read",
    metaTitle: "Whistleblowing and Ombudsman Guides | OmbudNow",
    metaDescription: "Complete guides on whistleblowing and ombudsman channels for small and medium businesses. Learn about requirements, differences, and implementation."
  }
};

export default function GuidesHub() {
  const { i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'pt';
  const t = content[currentLang] || content.pt;
  const guides = guidesData[currentLang] || guidesData.pt;
  
  const paths = {
    home: '/',
    about: currentLang === 'en' ? '/en/about' : '/pt/sobre',
    contact: currentLang === 'en' ? '/en/contact' : '/pt/contato',
    privacy: currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade',
    terms: currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso',
    guidesBase: currentLang === 'en' ? '/en/guides' : '/pt/guias',
  };

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: currentLang === 'en' ? 'Home' : 'Início', url: '/' },
    { name: currentLang === 'en' ? 'Guides' : 'Guias', url: paths.guidesBase }
  ]);

  return (
    <div className="min-h-screen bg-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-100/50 via-white to-white pointer-events-none" />
      <SEO
        title={t.metaTitle}
        description={t.metaDescription}
        canonical={paths.guidesBase}
        lang={currentLang}
        keywords={currentLang === 'en' 
          ? "whistleblowing guides, ombudsman articles, compliance content, ethics channel guide"
          : "guias canal de denúncias, artigos ouvidoria, conteúdo compliance, guia ética empresarial"}
        schemas={[organizationSchema, breadcrumbSchema]}
      />

      {/* Header */}
      <header className="relative z-50 border-b border-slate-200/50 bg-white/80 backdrop-blur-xl sticky top-0">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to={paths.home} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>{currentLang === 'en' ? 'Back' : 'Voltar'}</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="minimal" />
              <Link to={paths.home} className="flex items-center gap-3 group">
                <div className="w-10 h-10 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(168,85,247,0.4)] transition-all">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900 font-chivo">OmbudNow</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-slate-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              {currentLang === 'en' ? 'Knowledge Base' : 'Base de Conhecimento'}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-chivo tracking-tight">
              {t.title}
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t.subtitle}
            </p>
          </div>

          {/* Guides List */}
          <div className="space-y-6">
            {guides.map((guide) => (
              <Link
                key={guide.id}
                to={`${paths.guidesBase}/${guide.slug}`}
                className="block bg-white rounded-2xl p-6 md:p-8 border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors font-chivo">
                      {guide.title}
                    </h2>
                    <p className="text-slate-600 mb-4 leading-relaxed">
                      {guide.description}
                    </p>
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock className="w-4 h-4" />
                        {guide.readTime} {t.readTime}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-blue-600 font-medium">
                        {t.readMore}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 border-t border-slate-200/50">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-semibold">OMBUDNOW</span>
          </div>
          <p className="text-slate-500 text-sm">
            {currentLang === 'en' ? '© 2025 OmbudNow. All rights reserved.' : '© 2025 OmbudNow. Todos os direitos reservados.'}
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to={paths.about} className="text-slate-500 hover:text-white transition-colors">
              {currentLang === 'en' ? 'About' : 'Sobre'}
            </Link>
            <Link to={paths.privacy} className="text-slate-500 hover:text-white transition-colors">
              {currentLang === 'en' ? 'Privacy' : 'Privacidade'}
            </Link>
            <Link to={paths.contact} className="text-slate-500 hover:text-white transition-colors">
              {currentLang === 'en' ? 'Contact' : 'Contato'}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
